import { openDrawer } from 'Services/Drawer';
import openAgreement from 'App/helpers/openSubscriptionHelper';
import { openEditOrder } from 'App/components/EditOrder';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { shouldIgnoreRepeatOpen } from './entityOpenGuard';

const getCustomerId = (): string => {
	const id = Tools.AppService?.getCustomerId?.();
	return id != null ? String(id) : '';
};

const openEntityInNewTab = (stateName: string, id: number): boolean => {
	const href = Tools.$state?.href?.(stateName, { id, customerId: getCustomerId() });
	if (!href) return false;
	const absolute = href.startsWith('http') ? href : `${window.location.origin}${href}`;
	window.open(absolute, '_blank', 'noopener');
	return true;
};

export type CrmEntityOpts = {
	/** When the entity is `activity`, the activityType determines whether
	 *  the entity is a To-do, a Phone call, or a regular Activity. Each has
	 *  its own editing surface (drawer/modal). */
	activityType?: string | null;
};

const normalizeActivitySubtype = (raw: string | null | undefined): 'todo' | 'phonecall' | 'activity' => {
	const s = (raw ?? '')
		.trim()
		.toLowerCase()
		.replace(/[\s_-]/g, '');
	if (s === 'todo') return 'todo';
	if (s === 'phonecall') return 'phonecall';
	return 'activity';
};

/**
 * Dispatches a click on an "open this entity" link from chat surfaces
 * (WriteCard success state, mention chips in MessageBlock, etc.) to the
 * right modal/drawer/route. Returns false when the entity type is not
 * routable so the caller can hide the link.
 */
export function openCrmEntity(entityType: string, id: number, opts?: CrmEntityOpts): boolean {
	// Swallow a spammed click rather than stacking another modal/tab. Still
	// reports true: the entity IS routable, so the caller must keep the link.
	if (shouldIgnoreRepeatOpen(entityType, id)) return true;
	switch (entityType.toLowerCase()) {
		case 'client':
		case 'account':
		case 'company':
			return openEntityInNewTab('account.dashboard', id);
		case 'contact':
		case 'person':
			return openEntityInNewTab('contact.dashboard', id);
		case 'segment':
			return openEntityInNewTab('segment', id);
		case 'order':
		case 'deal':
		case 'opportunity':
			openEditOrder({ id });
			return true;
		case 'todo':
			openDrawer('EditTodo', { todo: { id } });
			return true;
		case 'phonecall':
			openDrawer('EditPhonecall', { activity: { id } });
			return true;
		case 'activity':
		case 'task': {
			const subtype = normalizeActivitySubtype(opts?.activityType);
			if (subtype === 'todo') {
				openDrawer('EditTodo', { todo: { id } });
				return true;
			}
			if (subtype === 'phonecall') {
				openDrawer('EditPhonecall', { activity: { id } });
				return true;
			}
			Tools.$upModal.open('editActivity', { id });
			return true;
		}
		case 'appointment':
		case 'meeting':
			openEditAppointment({ id });
			return true;
		case 'agreement':
			openAgreement({ agreementId: id });
			return true;
		case 'ticket':
			openDrawer('EditTicket', { ticketId: id });
			return true;
		case 'projectplan':
			openDrawer('EditProjectPlan', { projectPlanId: id });
			return true;
		default:
			return false;
	}
}

export function canOpenCrmEntity(entityType: string): boolean {
	switch (entityType.toLowerCase()) {
		case 'client':
		case 'account':
		case 'company':
		case 'contact':
		case 'person':
		case 'segment':
		case 'order':
		case 'deal':
		case 'opportunity':
		case 'activity':
		case 'task':
		case 'appointment':
		case 'meeting':
		case 'todo':
		case 'phonecall':
		case 'agreement':
		case 'ticket':
		case 'projectplan':
			return true;
		default:
			return false;
	}
}

/** Maps an entity alias to the lang-key suffix that names it. Keep aligned
 *  with canOpenCrmEntity — every routable entity must produce a label. */
export function crmEntityLabelKey(entityType: string, opts?: CrmEntityOpts): string | null {
	switch (entityType.toLowerCase()) {
		case 'client':
		case 'account':
		case 'company':
			return 'default.client';
		case 'contact':
		case 'person':
			return 'default.contact';
		case 'segment':
			return 'default.segment';
		case 'order':
		case 'deal':
		case 'opportunity':
			return 'default.order';
		case 'activity':
		case 'task': {
			const subtype = normalizeActivitySubtype(opts?.activityType);
			if (subtype === 'todo') return 'default.todo';
			if (subtype === 'phonecall') return 'default.phonecall';
			return 'default.activity';
		}
		case 'appointment':
		case 'meeting':
			return 'default.appointment';
		case 'todo':
			return 'default.todo';
		case 'phonecall':
			return 'default.phonecall';
		case 'agreement':
			return 'default.agreement';
		case 'ticket':
			return 'default.ticket';
		case 'projectplan':
			return 'default.projectPlan';
		default:
			return null;
	}
}
